import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { keys } from '../../api/keys'
import { emit } from '../../platform/analytics/analytics'
import { useToast } from '../../platform/feedback/ToastProvider'
import { useCategories } from '../categories/useCategories'
import type { MemorySuggestion, Txn } from './transactionWire'
import { failureOf, mayHaveCommitted, requestPatchAnswer, type SaveFailure } from './useAutosaveField'
import { useCategoryMemoryOffer } from './useCategoryMemoryOffer'
import { getTransaction } from './useTransaction'

type Outcome = { ok: true; suggestion: MemorySuggestion | null } | { ok: false; kind: SaveFailure }

/**
 * Set ONE row's category from a list (Transactions, Cashflow detail).
 *
 * The body is `category_id` alone — nothing the row did not ask to change.
 * Both caches are invalidated: the transactions lists AND every cashflow
 * report, because moving a row out of Uncategorised changes the detail page's
 * Spent tile, chart and count, and the overview behind it (bulk only
 * invalidates transactions; on Cashflow that would leave the totals stale).
 *
 * Undo PATCHes back the value the ROW held when the user acted — `null` for an
 * uncategorised row — never a value read later, which a refetch may already
 * have replaced. The undo toast carries no Undo of its own.
 *
 * **The PATCH is the detail page's** (`requestPatch`), and so is its failure
 * classification (#507, #576). EVERY failure refreshes both caches. A refusal
 * (4xx) did not commit: "Try again". A 5xx or no response may have committed,
 * so the row is re-read and the toast says what it holds — "set to" if the
 * change landed, the autosave's "It's back to …" if it did not, and "couldn't
 * confirm" if the re-read fails too. Analytics follow EditPanel's autosave:
 * `transaction_edited` / `transaction_edit_failed`, `field_kind: 'category'`,
 * enums only (rule 7).
 *
 * The answer to a set may carry a learned-memory offer (#753), shown as an "Apply to N other transactions?"
 * toast beside the set toast. Undo withdraws it, so an Apply after Undo cannot file the other rows under a
 * category the user has just taken back. The undo's own answer never offers.
 */
export function useQuickCategory() {
  const client = useQueryClient()
  const toast = useToast()
  const categories = useCategories()
  const memory = useCategoryMemoryOffer('list')

  const nameOf = useCallback(
    (id: string | null, fallback = 'the new category') =>
      id === null
        ? 'No category'
        : categories.status === 'success'
          ? (categories.data.find((c) => c.id === id)?.name ?? fallback)
          : fallback,
    [categories],
  )

  const patch = useCallback(async (id: string, category: string | null): Promise<Outcome> => {
    let answer
    try {
      answer = await requestPatchAnswer(id, { category_id: category })
    } catch (thrown) {
      const kind = failureOf(thrown)
      emit('transaction_edit_failed', { field_kind: 'category', error_kind: kind })
      return { ok: false, kind }
    }
    emit('transaction_edited', { field_kind: 'category' })
    return { ok: true, suggestion: answer.memorySuggestion }
  }, [])

  const refresh = useCallback(
    () =>
      Promise.all([
        client.invalidateQueries({ queryKey: keys.transactions.all() }),
        client.invalidateQueries({ queryKey: keys.cashflow.all() }),
      ]),
    [client],
  )

  const set = useCallback(
    async (txn: Txn, next: string | null) => {
      const before = txn.categoryId
      // Deliberately silent: nothing changed, so there is nothing to confirm or undo.
      if (before === next) return
      const outcome = await patch(txn.id, next)
      const suggestion = outcome.ok ? outcome.suggestion : null
      if (!outcome.ok) {
        // A refusal did not commit. A 5xx or a lost response may have: ask the server what the row holds.
        let held: { category: string | null } | 'unread' | undefined
        if (mayHaveCommitted(outcome.kind)) {
          held = await getTransaction(txn.id).then(
            (row) => ({ category: row.categoryId }),
            () => 'unread' as const,
          )
        }
        await refresh()
        if (held === undefined) {
          toast.show({ kind: 'error', message: 'Couldn’t change the category. Try again.' })
          return
        }
        if (held === 'unread') {
          toast.show({ kind: 'error', message: 'Couldn’t confirm the category change. The list has been refreshed.' })
          return
        }
        if (held.category !== next) {
          toast.show({ kind: 'error', message: `Couldn’t save the category. It’s back to ${nameOf(held.category, 'its previous category')}.` })
          return
        }
        // It committed: the row holds the new category, so this is a set like any other, with its Undo.
      } else {
        await refresh()
      }
      toast.show({
        kind: 'success',
        message: `${txn.merchant} set to ${nameOf(next)}`,
        action: {
          label: 'Undo',
          onAct: () => {
            if (suggestion !== null) memory.withdraw(suggestion)
            void (async () => {
              const undone = await patch(txn.id, before)
              await refresh()
              if (!undone.ok) toast.show({ kind: 'error', message: 'Couldn’t undo. The category is still changed.' })
            })()
          },
        },
      })
      memory.offer(suggestion)
    },
    [patch, refresh, toast, nameOf, memory],
  )

  return { set }
}
